

import { makeStyles } from 'tss-react/mui';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  Progress,
  SearchBar,
  SectionHeader,
  TableActionTypes,
  TableState,
  Typography,
  useDataTable,
} from 'web-library';
import { AxiosError } from 'axios';
import { RetrieveS3URLResponse } from '../../../../api/document/files/api-services/model';
import * as QUERIES from '../../../../api/candidate/result/@opadmin/queries-services/queries';
import DownloadFromS3Action from '../../../../components/buttons/downloadFromS3Action';
import { useTriggerMutate } from '../../../../hooks/query';
import { ResultReleasesSchema } from '../../../../models/@org-admin/ResultReleasesShema';
import { FileUploadState } from '../../../../models/File';
import RouteKey from '../../../../routes/key';
import { useAuditViewResultRequestMutation } from '../../../../api/candidate/result/@candidate/queries-services/queries';
import { GenericErrorResponse } from '../../../../api-refactored/typings';

const useStyles = makeStyles()((theme) =>
({
  titleRoot: {
    lineHeight: 1.3,
  },
  dataTableRoot: {
    width: '100%',
  },
  dialogContainer: {
    display: 'flex',
    margin: theme.spacing(2),
    alignItems: 'center',
    justifyContent: 'center',
  },
  dialogContent: {
    padding: theme.spacing(1),
  },
  dialogGrid:{
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center'
  },
  searchContainer: {
    paddingBottom: theme.spacing(2),
  },
  viewDetailsButton: {
    color: theme.color.link.linkDefault01,
  },
}));

export interface OrgAdminResultReleaseProps {
  /** define  props here
   *
   */
}

/** define default columns
 * */
const customTableColumns: MUIDataTableColumnDef[] = [
  {
    name: 'candidateName',
    label: 'Candidate Name',
  },
  {
    name: 'candidateId',
    label: 'NRIC/FIN/Passport No.',
  },
  {
    name: 'indexNo',
    label: 'Index No.',
  },
  {
    name: 'examLevel',
    label: 'Exam Level',
  },
];

/** to added view result action link in table
 * */
const renderActionColumn = (
  classes: any,
  onViewDetails: (data: ResultReleasesSchema) => void,
  state: TableState
) => ({
  label: ' ',
  name: 'search',
  options: {
    customBodyRenderLite: (dataIndex: number, rowIndex: number) => (
      <div style={{ marginLeft: 8 }}>
        <Button
          className={classes.viewDetailsButton}
          id={`button-resultRelease-row${rowIndex}`}
          variant="text"
          onClick={() => {
            onViewDetails(state.data[dataIndex]);
          }}
          size="small"
          color="primary"
        >
          View details
        </Button>
      </div>
    ),
  },
});

const { admin } = RouteKey.child;
const { orgAdminIndividualResult, orgAdminResultRelease } = RouteKey.admin.child;

const OrgAdminResultRelease: React.FC<OrgAdminResultReleaseProps> = () => {
  const { classes } = useStyles();

  const [fileUUID, setFileUUID] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fileDownloadStatus, setFileDownloadStatus] = useState<FileUploadState>(
    FileUploadState.IDLE
  );

  const { state, dispatch: tableDispatch, tOptions, selectionToolbar } = useDataTable();

  const customOptions = useMemo<MUIDataTableOptions>(
    () => ({
      ...tOptions,
      selectableRows: 'multiple',
      pagination: true,
      filter: false,
      filterType: 'multiselect',
      // need to put to make selection replacement work
      selectToolbarPlacement: 'replace',
    }),
    [tOptions]
  );

  const history = useHistory();
  const { error } = RouteKey.child;
  const { generic } = RouteKey.error.child;

  /** Query to search result release
   * */
  const { isLoading, mutate } = QUERIES.usePostOrgAdminResultReleaseSearchParam(
    (response) => {
      if (response && response.data) {
        tableDispatch({
          type: 'UPDATE_DATA',
          payload: {
            data: response.data.content,
            count: response.data.totalElements,
          },
        } as TableActionTypes);
      }
    },
    (err: AxiosError<GenericErrorResponse>) => {
      const code = err.response?.data?.code?.toString();
      if (code === '1007') history.push(`/${error}/${generic}`);
    }
  );

  const callMutation = useCallback(() => {
    if (state.search && state.search.toString().length >= 3) {
      mutate({ page: state.page, size: state.size, keywords: state.search });
    } else {
      mutate({ page: state.page, size: state.size, keywords: '*' });
    }
  }, [mutate, state.page, state.search, state.size]);
  const setTriggerMutate = useTriggerMutate(callMutation);

  useEffect(() => {
    setTriggerMutate((prev) => prev + 1);
  }, [state.search, state.page, state.size, setTriggerMutate]);

  /** Query to post Bulk Download Data
   * */
  const { mutate: bulkDownloadMutate } = QUERIES.usePostBulkDownloadOrgAdminResultRelease(
    (response) => {
      setFileUUID(response.uuid);
    },
    () => null
  );

  /** to view individual result release
   * */
  const onViewDetails = useCallback(
    (data: ResultReleasesSchema) => {
      history.push(`/${admin}/${orgAdminResultRelease.route}/${orgAdminIndividualResult.route}`, {
        indexNo: data.indexNo,
      });
    },
    [history]
  );

  /** to define bulk download
   * */
  const onBulkDownload = useCallback(
    (selectedIndexNo: string[]) => {
      bulkDownloadMutate(selectedIndexNo);
    },
    [bulkDownloadMutate]
  );

  /** to handle dialog close action
   * */
  const onDialogExist = () => {
    setDialogOpen(false);
    setFileUUID('');
  };

  /** handle download action
   * */
  const onDownloadTemplate = (response: RetrieveS3URLResponse) => {
    setDialogOpen(true);
    if (response.fileStatus === FileUploadState.SUCCESS) {
      setFileUUID('');
      setDialogOpen(false);
    }
    setFileDownloadStatus(response.fileStatus as FileUploadState);
  };

  const viewResult = useAuditViewResultRequestMutation();

  const viewResultMutation = useCallback(() => {
    viewResult.mutate();
  }, [viewResult]);

  const setTriggerResultMutate = useTriggerMutate(viewResultMutation);

  useEffect(() => {
    setTriggerResultMutate((prev) => prev + 1);
  }, [setTriggerResultMutate]);

  /** to added view result action link in table
   * */
  const renderCustomColumns = useMemo(() => {
    const customBody = renderActionColumn(classes, onViewDetails, state);
    return [...customTableColumns, { ...customBody }];
    /*  eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [onViewDetails, state.data]);

  return (<>
    <SectionHeader title="Candidate Results List" />
    <Box className={classes.searchContainer}>
      <SearchBar
        placeholder="Search by Name/NRIC/FIN/Passport No./Index No."
        name="searchInput"
        ariaLabel="searchInput"
        defaultValue={state.search}
        onSearch={(str) => {
          tableDispatch({
            type: 'UPDATE_SEARCH',
            payload: str,
          });
        }}
      />
    </Box>
    <MUIDataTable
      isLoading={isLoading}
      // @ts-ignore
      state={state}
      dispatch={tableDispatch}
      columns={renderCustomColumns}
      // @ts-ignore
      data={state.data}
      options={customOptions}
      components={{
        TableToolbarSelect: () =>
          selectionToolbar([
            <DownloadFromS3Action
              actionType="link"
              key={fileUUID}
              requestRoute={{ data: fileUUID, url: '/api/s3/files' }}
              label="Download"
              onClick={() => {
                onBulkDownload(
                  Object.keys(state.rowsSelected).map((i) => state.rowsSelected[i].indexNo)
                );
              }}
              onDownload={(status) => onDownloadTemplate(status)}
            />,
          ]),
      }}
    />
    <Dialog
      id="opadmin-download-dialog"
      className={classes.dialogContainer}
      fullWidth
      open={dialogOpen}
      maxWidth="sm"
    >
      <DialogContent className={classes.dialogContent}>
        <Grid wrap="wrap" container direction="column" spacing={2} className={classes.dialogGrid}>
          <Grid xs={12}>
            {(fileDownloadStatus === 'UPLOADING' ||
              fileDownloadStatus === 'IDLE' ||
              fileDownloadStatus === 'GENERATING') && (
                <Typography display="inline" variant="h4">
                  Processing
                </Typography>
              )}
            {(fileDownloadStatus === 'TIMEOUT' || fileDownloadStatus === 'FAILED') && (
              <Typography display="inline" variant="h4">
                Failed to download, please try again later.{' '}
              </Typography>
            )}
          </Grid>
          {(fileDownloadStatus === 'UPLOADING' ||
            fileDownloadStatus === 'IDLE' ||
            fileDownloadStatus === 'GENERATING') && (
              <Grid xs={12}>
                <Progress color="secondary" />
              </Grid>
            )}
        </Grid>
      </DialogContent>
      {(fileDownloadStatus === 'TIMEOUT' || fileDownloadStatus === 'FAILED') && (
        <DialogActions id="oppadmin-dialog-action">
          <Grid container justify="center" wrap="nowrap" direction="row">
            <Button variant="contained" onClick={onDialogExist}>
              Ok
            </Button>
          </Grid>
        </DialogActions>
      )}
    </Dialog>
  </>);
};
export default OrgAdminResultRelease;
